import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { usePersistentState } from 'react-persistent-state'
import { QueryClient, QueryClientProvider, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { DialogsRoot } from '@/dialogs/DialogsRoot.tsx'
import { ChattingProvider } from '@/modules/chatting/state/chatting-provider'
import { ChatWindow } from '@/modules/chatting/ui/chat-window/ChatWindow'
import { FanInfo } from '@/modules/chatting/ui/chat-window/fan-info'
import { ChattingHeader } from '@/modules/chatting/ui/header'
import { ChattingSidebar } from '@/modules/chatting/ui/sidebar/ChattingSidebar'
import { handleError } from '@/utils/error'

const shouldShowFanInfo = () => {
  return window.innerWidth >= 768
}

export const ChattingPage = () => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const toast = useToast()
  const client = useQueryClient()
  const [isFanInfoVisible, setIsFanInfoVisible] = usePersistentState(
    shouldShowFanInfo(),
    { storageKey: 'isFanInfoVisible' },
  )
  const [isFanInfoVisibilityTouched, setIsFanInfoVisibilityTouched] =
    useState(false)
  const windowResizeHandlerRef = useRef<(() => void) | null>()

  const newClient = useMemo(
    () =>
      new QueryClient({
        queryCache: client.getQueryCache(),
        mutationCache: client.getMutationCache(),
        defaultOptions: {
          ...client.getDefaultOptions(),
          mutations: {
            ...client.getDefaultOptions().mutations,
            onError: (error) => {
              handleError(error)
            },
          },
          queries: {
            ...client.getDefaultOptions().queries,
            onError: (error) => {
              handleError(error)
            },
          },
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, toast],
  )

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((prev) => !prev)
  }

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false)
  }, [])

  useEffect(
    () => {
      const cleanup = () => {
        if (windowResizeHandlerRef.current) {
          window.removeEventListener('resize', windowResizeHandlerRef.current)
          windowResizeHandlerRef.current = null
        }
      }

      const windowResizeHandler = () => {
        setIsFanInfoVisible(shouldShowFanInfo())
      }

      cleanup()

      if (!isFanInfoVisibilityTouched) {
        windowResizeHandlerRef.current = windowResizeHandler
        window.addEventListener('resize', windowResizeHandlerRef.current)
      }

      return cleanup
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFanInfoVisibilityTouched],
  )

  const toggleFanInfo = () => {
    setIsFanInfoVisible((prev) => !prev)
    setIsFanInfoVisibilityTouched(true)
  }

  const closeFanInfo = () => {
    setIsFanInfoVisible(false)
    setIsFanInfoVisibilityTouched(true)
  }

  return (
    <QueryClientProvider client={newClient}>
      <ChattingProvider>
        <DialogsRoot />
        <ChattingHeader toggleMobileSidebar={toggleMobileSidebar} />
        <div className="relative flex h-[calc(100vh-55px)] items-stretch">
          <ChattingSidebar
            isMobileSidebarOpen={isMobileSidebarOpen}
            closeMobileSidebar={closeMobileSidebar}
          />
          <ChatWindow
            isFanInfoVisible={isFanInfoVisible}
            toggleFanInfo={toggleFanInfo}
          />
          <FanInfo
            open={isFanInfoVisible}
            onClose={closeFanInfo}
          />
        </div>
      </ChattingProvider>
    </QueryClientProvider>
  )
}
